import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';

import { SizingHold, SizingHoldMovement } from '../../core/applications/sizing-hold.types';
import { IconComponent } from '../icon/icon';
import { ZarPipe } from '../pipes/zar.pipe';
import { ReleaseHoldDialogComponent, ReleaseHoldDialogData } from './release-hold-dialog';

/**
 * How close a re-raise has to be to a release to count as the SAME request's
 * doing. A Kickstart release re-scores inline, so the new hold lands in the
 * same second; a minute is generous headroom for a slow run, and still far
 * short of an Ops user coming back to the file later.
 */
const RE_HELD_WINDOW_MS = 60_000;

/**
 * The declared-account hold, rendered for Ops on all three surfaces (FLOW-283).
 *
 * 🚨 **A PRE-SIZING hold, in `DOC-58`'s terms (`UI-DA-58-02`), and therefore
 * this card carries NO SIZE ELEMENT OF ANY KIND** — no figure, no range, no
 * "estimated", no greyed placeholder, no captioned empty field where a number
 * would go (`UI-DA-58-01`). Where a hold is open, sizing is blocked and **no
 * facility size exists**; a held place for an absent number asserts that the
 * number exists. That is the one thing a future edit to this component must not
 * undo, and the spec is not a nicety — it is the difference between "we have
 * not worked out a figure" and "we are withholding one from you".
 *
 * 🚨 **`UI-DA-58-04`: recorded by a named actor, as at a recorded moment —
 * never "now" and never as a live system state.** The account-declaration
 * conditions are DORMANT on the system limb and LIVE on the Ops limb
 * (`FC-DET-021`), so the honest rendering is a human record carrying its own
 * timestamp. Hence `raised_at` is always shown, and the engine — which raises
 * the Kickstart lane's holds with no human in the request — is named as an
 * actor rather than left blank.
 *
 * 🚨 **Ops-only (N4).** `DOC-58` §6: the trigger text NEVER reaches the client,
 * who is told only that the application is in progress and CapX will be in
 * contact. Do not import this component into a client-facing page.
 *
 * Presentational: the host owns the POST, because the release endpoint differs
 * per lane — Kickstart's is keyed on the KS application (and on a
 * pre-application that means the INDICATIVE DRAFT, not the intake), Debtors
 * Finance's on the facility. The card collects the note and emits it.
 */
@Component({
  selector: 'app-sizing-hold-card',
  imports: [MatButtonModule, DatePipe, IconComponent, ZarPipe],
  templateUrl: './sizing-hold-card.html',
  styleUrl: './sizing-hold-card.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SizingHoldCardComponent {
  readonly hold = input<SizingHold | null>(null);
  /**
   * Whether the client has affirmed its account list is complete (FLOW-284).
   *
   * 🚨 **Defaults to `false`, which WITHHOLDS the release action** — the safe
   * way round. A host that forgets to bind this shows the ask and no button,
   * which is merely unhelpful; the reverse would offer an Ops user a release
   * the server refuses, which is the defect this ticket exists for.
   */
  readonly affirmationInForce = input(false);

  /** The host's POST of the release note to its own lane's endpoint. */
  readonly saveRelease = input.required<(releaseNote: string) => Observable<unknown>>();

  private readonly dialog = inject(MatDialog);

  /**
   * Whether anything is rendered at all.
   *
   * ⚠️ **`is_open` is checked, not merely the record's presence.** A released
   * hold is history — release is a state transition on an append-only row, not
   * a delete — and rendering one would tell the reviewer the file is waiting
   * when it is not. The Ops payloads only ever carry the open hold, so this is
   * a belt on that.
   */
  protected readonly isHeld = computed(() => this.hold()?.is_open === true);

  /**
   * Who recorded it. `UI-DA-58-04` needs a named actor, and the engine is one.
   *
   * ⚠️ A null `raised_by_name` is NOT a missing value — `raised_by_system` says
   * the engine determined it, which is the Kickstart lane's ordinary case. "—"
   * here would read as "we failed to capture who".
   */
  protected readonly actor = computed(() => {
    const hold = this.hold();
    if (!hold) {
      return '';
    }
    return hold.raised_by_system ? 'the engine' : (hold.raised_by_name ?? 'the engine');
  });

  /** Ops' next step with the client, in the backend's own words (§6). */
  protected readonly ask = computed(() => {
    const ask = this.hold()?.evidence?.ask;
    return typeof ask === 'string' && ask.trim() ? ask : null;
  });

  /**
   * The sources that tripped limb (b), each with its direction and share, read
   * off the record (DOC-77 §3). Empty for limb (a) and for a hold raised before
   * FLOW-343, whose evidence carries no movements.
   */
  protected readonly movements = computed<SizingHoldMovement[]>(() => {
    const movements = this.hold()?.evidence?.movements;
    return Array.isArray(movements) ? movements : [];
  });

  protected readonly grossCredits = computed(() => this.hold()?.evidence?.gross_credits ?? null);

  /** Which way the money moved, in words. A label for a recorded value — not a judgement. */
  protected directionLabel(direction: SizingHoldMovement['direction']): string {
    return direction === 'out' ? 'Money going out to' : 'Money coming in from';
  }

  /**
   * What is held, per lane — read off the record, never inferred (`UI-DA-58-03`).
   *
   * Kickstart blocks the engine run; Debtors Finance has no automated sizing to
   * block, so what waits there is the offer. Naming the wrong one would tell a
   * DF reviewer a figure is pending when no figure is ever computed on that lane.
   */
  protected readonly consequence = computed(() =>
    this.hold()?.lane === 'DEBTORS_FINANCE'
      ? 'No offer may be made while this hold is open.'
      : 'No indicative figure is worked out while this hold is open.',
  );

  /**
   * Whether a release may be offered at all.
   *
   * 🔑 **The whole ticket is this one predicate.** Limb (a) —
   * `DECLARED_ACCOUNTS_NOT_ON_FILE` — is re-read by the server on every
   * release, so releasing it before the client affirms its list raises an
   * identical hold seconds later; the API refuses such a release with a 409.
   * On staging that happened seven times in six minutes, each one leaving a
   * throwaway release note behind, because the button was always there.
   *
   * ⚠️ **`UNDECLARED_ACCOUNT_MOVEMENT` always releases**, whatever the
   * affirmation says. That limb turns on what Ops judged about a movement, not
   * on an act the client has to perform, so the Ops release IS the answer to
   * it — withholding the button there would leave the file with no way out.
   *
   * ⚠️ And an affirmed limb-(a) hold releases too: the affirmation arrived
   * after the hold was raised, so the hold is stale and a release is exactly
   * what clears it. `true` here is not "nothing is held", it is "releasing
   * this will stick".
   */
  protected readonly canRelease = computed(() => {
    const hold = this.hold();
    if (!hold) {
      return false;
    }
    return hold.reason !== 'DECLARED_ACCOUNTS_NOT_ON_FILE' || this.affirmationInForce();
  });

  /**
   * When this hold was raised and what brought it back, if it came straight
   * back after a release. `null` otherwise — including when the gap is longer
   * than a minute, which is an Ops user returning to the file rather than the
   * server re-raising inside the release request.
   *
   * ⚠️ **The rendered moment is `raised_at`, not the release's.** The line
   * answers "why is this here again", and the answer is that the file was
   * re-read the instant the release landed; `previous_released_at` is what
   * proves the two are adjacent, not what is shown.
   *
   * 🚨 **One sentence per limb, because the two re-raises mean different
   * things** (FLOW-287). Limb (a) comes back because the list is still
   * unconfirmed — the client has not acted. Limb (b) now comes back ONLY over
   * movement no release covers: since FLOW-287 the server does not re-raise
   * over receipts an Ops release already judged, so a limb-(b) hold appearing
   * here is a genuinely NEW finding and not the loop that ticket closed.
   * Rendering limb (a)'s sentence over it would report the opposite of what
   * the server found.
   */
  protected readonly reHeld = computed(() => {
    const hold = this.hold();
    const previous = hold?.previous_released_at;
    if (!hold || !previous) {
      return null;
    }
    const gapMs = Date.parse(hold.raised_at) - Date.parse(previous);
    if (Number.isNaN(gapMs) || gapMs < 0 || gapMs > RE_HELD_WINDOW_MS) {
      return null;
    }
    return {
      at: hold.raised_at,
      detail:
        hold.reason === 'UNDECLARED_ACCOUNT_MOVEMENT'
          ? 'new movement was found.'
          : 'the list was still unconfirmed.',
    };
  });

  protected openRelease(): void {
    const data: ReleaseHoldDialogData = { save: this.saveRelease() };
    this.dialog.open(ReleaseHoldDialogComponent, { data });
  }
}
